import imgCampsite1 from '../../assets/Images/hostCampsite.jpg';
import imgFarm from '../../assets/Images/landcamp_test.jpg';

export enum ProductWeekendOffset {
    FLEXIBLE = -1,
    NEXT_WEEKEND = 0,
    WEEKEND_AFTER_NEXT = 1,
}

type AgentProductBase = {
    productId: number;
    name: string;
    description: string;
    image: string | null;
    showCurator: boolean;
    curatorImage: string | null;
    weekendOffset: ProductWeekendOffset;
};

export type CuratedSelectionProduct = AgentProductBase & {
    curationId: number;
    compoundTypes: string[];
};

export type AgentProduct = CuratedSelectionProduct;

type ProductApiModel = {
    productId: number;
    productName: string;
    description: string;
    curationId: number;
    compoundTypes: string[];
    image?: string | null;
    showCurator: boolean;
    curatorImage?: string | null;
    weekendOffset: number;
};

export type SearchCardProduct = {
    title: string;
    description?: string;
    regionId: number;
    compoundTypes: string[];
    weekendOffset?: number;
    curationId?: number;
    imageUrl: string;
    showCurator: boolean;
    curatorImageUrl?: string;
};

const getAuthHeaders = () => {
    const token = sessionStorage.getItem('token');

    return {
        'Content-Type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
    };
};

const getApiUrl = (path: string) => `${process.env.REACT_APP_API_URL ?? ''}${path}`;

let inFlightGetAgentProducts: Promise<AgentProduct[]> | null = null;

const parseJsonResponse = async <T>(response: Response): Promise<T> => {
    if (!response.ok) {
        let message = `Request failed with status ${response.status}`;
        try {
            const text = await response.text();
            if (text) {
                message = `${message}: ${text}`;
            }
        } catch {
            // ignore parsing errors, keep status-based message
        }
        throw new Error(message);
    }

    return response.json();
};

const toWeekendOffset = (value: number): ProductWeekendOffset => {
    if (value === ProductWeekendOffset.NEXT_WEEKEND || value === ProductWeekendOffset.WEEKEND_AFTER_NEXT) {
        return value;
    }

    return ProductWeekendOffset.FLEXIBLE;
};

const toImageDataUrl = (base64Image: string | null | undefined): string | undefined => {
    if (!base64Image) {
        return undefined;
    }

    const normalized = base64Image.trim();
    if (!normalized) {
        return undefined;
    }

    if (normalized.startsWith('/9j/')) {
        return `data:image/jpeg;base64,${normalized}`;
    }

    if (normalized.startsWith('iVBORw0KGgo')) {
        return `data:image/png;base64,${normalized}`;
    }

    if (normalized.startsWith('R0lGOD')) {
        return `data:image/gif;base64,${normalized}`;
    }

    if (normalized.startsWith('UklGR')) {
        return `data:image/webp;base64,${normalized}`;
    }

    return `data:image/jpeg;base64,${normalized}`;
};

const toAgentProduct = (product: ProductApiModel): AgentProduct => ({
    productId: product.productId,
    name: product.productName,
    description: product.description,
    curationId: product.curationId,
    compoundTypes: product.compoundTypes ?? [],
    image: product.image ?? null,
    showCurator: product.showCurator,
    curatorImage: product.curatorImage ?? null,
    weekendOffset: toWeekendOffset(product.weekendOffset),
});

const toApiProduct = (product: AgentProduct): ProductApiModel => ({
    productId: product.productId,
    productName: product.name,
    description: product.description,
    curationId: product.curationId,
    compoundTypes: product.compoundTypes,
    image: product.image,
    showCurator: product.showCurator,
    curatorImage: product.curatorImage,
    weekendOffset: product.weekendOffset,
});

export const getDefaultProducts = (): AgentProduct[] => [
    {
        productId: 0,
        name: 'Campen in der Zentralschweiz',
        description: 'Frische Luft und Höhenmeter.',
        curationId: 1,
        compoundTypes: ['CAMPSITE'],
        image: imgFarm,
        showCurator: false,
        curatorImage: null,
        weekendOffset: ProductWeekendOffset.FLEXIBLE,
    },
    {
        productId: 0,
        name: 'Nikos Lieblingsplätze',
        description: 'Starte übernächste Woche in Dein Abenteuer',
        curationId: 1,
        compoundTypes: [],
        image: imgCampsite1,
        showCurator: true,
        curatorImage: null,
        weekendOffset: ProductWeekendOffset.FLEXIBLE,
    },
];

export const getAgentProducts = async (): Promise<AgentProduct[]> => {
    if (inFlightGetAgentProducts) {
        return inFlightGetAgentProducts;
    }

    inFlightGetAgentProducts = (async () => {
        const response = await fetch(getApiUrl('/curationproducts'), {
            headers: getAuthHeaders(),
        });

        const products = await parseJsonResponse<ProductApiModel[]>(response);
        return products.map(toAgentProduct);
    })();

    try {
        return await inFlightGetAgentProducts;
    } finally {
        inFlightGetAgentProducts = null;
    }
};

export const createProduct = async (product: AgentProduct): Promise<AgentProduct> => {
    const response = await fetch(getApiUrl('/curationproducts'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(toApiProduct(product)),
    });

    const created = await parseJsonResponse<ProductApiModel>(response);
    return toAgentProduct(created);
};

export const updateProduct = async (product: AgentProduct): Promise<AgentProduct> => {
    const response = await fetch(getApiUrl(`/curationproducts/${product.productId}`), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(toApiProduct(product)),
    });

    const updated = await parseJsonResponse<ProductApiModel>(response);
    return toAgentProduct(updated);
};

export const deleteProduct = async (productId: number): Promise<void> => {
    const response = await fetch(getApiUrl(`/curationproducts/${productId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });

    if (!response.ok) {
        throw new Error(`Delete failed with status ${response.status}`);
    }
};

export const toSearchCardProducts = (products: AgentProduct[]): SearchCardProduct[] => {
    return products
        .map((product) => ({
            title: product.name,
            description: product.description,
            regionId: 0,
            compoundTypes: product.compoundTypes,
            curationId: product.curationId,
            weekendOffset: product.weekendOffset,
            imageUrl: toImageDataUrl(product.image) ?? imgCampsite1,
            showCurator: product.showCurator,
            curatorImageUrl: toImageDataUrl(product.curatorImage),
        }));
};

export const createEmptyCuratedSelectionProduct = (): CuratedSelectionProduct => ({
    productId: 0,
    name: '',
    description: '',
    image: null,
    curationId: 0,
    compoundTypes: [],
    showCurator: false,
    curatorImage: null,
    weekendOffset: ProductWeekendOffset.FLEXIBLE,
});
